import React, { useLayoutEffect, useRef, useState } from 'react'

/**
 * Horizontal step indicator shared by the Create Template and New Request
 * wizards. Purely presentational: each step says whether it can be clicked
 * and what clicking does, so each wizard keeps its own navigation rules.
 *
 * Connector lines are a fixed, short length and the stepper sits at the left,
 * so wide screens don't stretch the steps apart.
 *
 * Motion (index.css, "Wizard stepper"): a highlight glides to the current
 * step (a full-width layer whose clip-path moves, so nothing re-lays out), connector lines fill as steps complete, a finished step draws its
 * check mark, and the current step's node pulses once when it becomes current.
 * Everything animates transform/opacity only, and is off under reduced motion.
 *
 * steps: [{ key, label, icon, disabled, onSelect }]
 * current: 1-based index of the current step
 */
export default function WizardStepper({ steps, current, ariaLabel }) {
  const listRef = useRef(null)
  const buttonRefs = useRef([])
  const [pill, setPill] = useState(null)
  const placedOnce = useRef(false)

  // Measure the current step's button so the highlight can sit behind it,
  // and re-measure when the stepper resizes (labels, window width).
  useLayoutEffect(() => {
    const list = listRef.current
    if (!list) return undefined
    const place = () => {
      const btn = buttonRefs.current[current - 1]
      if (!btn) return
      const a = list.getBoundingClientRect()
      const b = btn.getBoundingClientRect()
      setPill({ left: b.left - a.left, right: a.right - b.right, animate: placedOnce.current })
      placedOnce.current = true
    }
    place()
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(place) : null
    ro?.observe(list)
    return () => ro?.disconnect()
  }, [current, steps.length])

  return (
    <ol ref={listRef} className="wz relative flex w-max items-center" aria-label={ariaLabel}>
      {pill && (
        <span
          aria-hidden="true"
          className={`wz-pill ${pill.animate ? 'wz-pill--glide' : ''}`}
          style={{ clipPath: `inset(0 ${pill.right}px 0 ${pill.left}px round 9999px)` }}
        />
      )}
      {steps.map((step, idx) => {
        const n = idx + 1
        const state = n < current ? 'done' : n === current ? 'current' : 'upcoming'
        const StepIcon = step.icon
        const last = idx === steps.length - 1
        return (
          <li key={step.key || step.label} className="flex items-center" data-create-stagger>
            <button
              ref={(el) => { buttonRefs.current[idx] = el }}
              type="button"
              onClick={() => { if (!step.disabled) step.onSelect?.() }}
              disabled={step.disabled}
              aria-current={state === 'current' ? 'step' : undefined}
              data-state={state}
              className="wz-step group/step relative z-[1] flex flex-shrink-0 items-center gap-2 rounded-full py-1 pl-1 pr-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40 disabled:cursor-not-allowed"
            >
              <span className="wz-node relative inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full">
                {state === 'done' ? (
                  <svg key="done" viewBox="0 0 24 24" className="wz-check h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M5 12.5l4.5 4.5L19 7.5" />
                  </svg>
                ) : (
                  <StepIcon key={state} className="wz-icon h-3 w-3" aria-hidden="true" />
                )}
              </span>
              <span className="wz-label whitespace-nowrap text-[12.5px] font-semibold">{step.label}</span>
            </button>
            {!last && (
              <span aria-hidden="true" className="wz-line mx-2 h-[2px] w-[clamp(2rem,5vw,6rem)] overflow-hidden rounded-full">
                <span className={`wz-line__fill ${n < current ? 'is-filled' : ''}`} />
              </span>
            )}
          </li>
        )
      })}
    </ol>
  )
}
